import { orderBy } from 'lodash';

import DomUtils from '../../common/utils/DomUtils';
import CoreUtils from '../../common/utils/CoreUtils';
import ReactUtils from '../../common/utils/ReactUtils';

const toolTipDebounceDelayMs = 50;

export default class ScheduleSelectionController {
  constructor(keyProperty, enableEditProperty, dataGridRef, isAltRowIndex, toolTipDelayMs, onQuerySelectableRow, onCancelPopups, onShowFieldTooltip, onHideFieldTooltip) {
    this.keyProperty = keyProperty;
    this.enableEditProperty = enableEditProperty;
    this.dataGridRef = dataGridRef;
    this.isAltRowIndex = isAltRowIndex;
    this.toolTipDelayMs = toolTipDelayMs;
    this.onQuerySelectableRow = onQuerySelectableRow;
    this.onCancelPopups = onCancelPopups;
    this.onShowFieldTooltip = onShowFieldTooltip;
    this.onHideFieldTooltip = onHideFieldTooltip;

    this.enableSelection = false;
    this.activateSelection = false;

    this.toolTipCell = null;
    this.toolTipShowTimeout = null;
    this.toolTipHideTimeout = null;
    this.toolTipActive = false;

    this.dataPivotCell = null;
    this.miscPivotElement = null;
    this.pivotRangeTimeoutToken = 1;

    this.pendingSelectInEffect = false;
    this.pendingSelectCellStart = null;
    this.pendingSelectCellEnd = null;
    this.pendingSelectRange = {};

    this.currentSelectRange = {};

    this.mouseDownHandler = this.onMouseDown.bind(this);
    this.mouseUpHandler = this.onMouseUp.bind(this);
  }

  dispose() {
    this.cancelToolTipShowTimeout();
    this.cancelToolTipHideTimeout();
  }

  resolveDataGrid() {
    return ReactUtils.resolveReference(this.dataGridRef);
  }

  setEnableSelection(enable) {
    this.enableSelection = enable;
  }

  setActivateSelection(activate) {
    this.activateSelection = activate;
  }

  selectCurrentCell() {
    if (this.dataPivotCell === null)
      return;

    this.clearCurrentSelection();
    this.initiatePendingSelect(this.dataPivotCell);
    this.finalizePendingSelect(this.dataPivotCell);
  }

  onCellHoverChanged(event) {
    if (CoreUtils.isUndefinedOrNull(event.data))
      return;

    this.hideToolTip();

    this.disengageDataPivotCell();
    this.disengageMiscPivotElement();

    this.handleSelectionTracking(event);
    this.handleTooltipTracking(event);
  }

  handleSelectionTracking(event) {
    if (!this.enableSelection || !this.activateSelection)
      return;

    if (event.eventType === 'mouseover') {
      if (event.rowType === 'data') {

        this.engageDataPivotCell({
          scheduleId: event.data[this.keyProperty],
          enableEditValues: event.data[this.enableEditProperty],
          dataField: event.column.dataField,
          rowIndex: event.rowIndex,
          columnIndex: event.columnIndex,
          isAltRow: event.isAltRow,
          element: event.cellElement
        });

        if (this.pendingSelectInEffect) {
          this.pendingSelectCellEnd = this.dataPivotCell;
          this.updatePendingSelectRange();
        }
      } else {
        this.engageMiscPivotElement(event.cellElement);
      }

      this.resetPivotRangeTimeout();
    } else {
      this.setPivotRangeTimeout();
    }
  }

  engageDataPivotCell(dataPivotCell) {
    if (dataPivotCell === null || !dataPivotCell.enableEditValues)
      return;

    this.dataPivotCell = dataPivotCell;

    if (!this.isCellSelected(this.dataPivotCell.scheduleId, this.dataPivotCell.dataField))
      this.setElementHovered(this.dataPivotCell.element, this.dataPivotCell.scheduleId, this.dataPivotCell.isAltRow);

    this.dataPivotCell.element.addEventListener('mousedown', this.mouseDownHandler, true);
    this.dataPivotCell.element.addEventListener('mouseup', this.mouseUpHandler, true);
  }

  disengageDataPivotCell() {
    if (this.dataPivotCell === null)
      return;

    if (!this.isCellSelected(this.dataPivotCell.scheduleId, this.dataPivotCell.dataField))
      this.setElementNormal(this.dataPivotCell.element, this.dataPivotCell.isAltRow);

    this.dataPivotCell.element.removeEventListener('mouseup', this.mouseUpHandler, true);
    this.dataPivotCell.element.removeEventListener('mousedown', this.mouseDownHandler, true);

    this.dataPivotCell = null;
  }

  engageMiscPivotElement(element) {
    if (element === null)
      return;

    this.miscPivotElement = element;
    this.miscPivotElement.addEventListener('mouseup', this.mouseUpHandler, true);
  }

  disengageMiscPivotElement() {
    if (this.miscPivotElement === null)
      return;

    this.miscPivotElement.removeEventListener('mouseup', this.mouseUpHandler, true);
    this.miscPivotElement = null;
  }

  setPivotRangeTimeout() {
    setTimeout(this.onPivotRangeTimeout.bind(this), 500, this.pivotRangeTimeoutToken);
  }

  resetPivotRangeTimeout() {
    this.pivotRangeTimeoutToken++;
  }

  onPivotRangeTimeout(pivotRangeTimeoutToken) {
    if (pivotRangeTimeoutToken !== this.pivotRangeTimeoutToken)
      return;

    this.finalizePendingSelect(null);
  }

  updateCellAppearance(scheduleId, dataField, isAltRow, element) {
    if (this.isCellSelected(scheduleId, dataField))
      this.setElementSelected(element, scheduleId, isAltRow);
  }

  onMouseDown(event) {
    if (this.dataPivotCell === null || event.button !== 0)
      return;

    this.hideToolTip();

    if (CoreUtils.isDefinedAndNotNull(this.onCancelPopups))
      this.onCancelPopups();

    if (!this.isRowSelectable(this.dataPivotCell.scheduleId))
      return;

    if (this.isOnlyCellSelected(this.dataPivotCell.scheduleId, this.dataPivotCell.dataField)) {
      this.clearCurrentSelection();
    } else {
      if (!event.ctrlKey)
        this.clearCurrentSelection();

      this.initiatePendingSelect(this.dataPivotCell);
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  onMouseUp(event) {
    if (event.button !== 0)
      return;

    if (!this.pendingSelectInEffect)
      return;

    this.finalizePendingSelect(this.dataPivotCell);
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  initiatePendingSelect(dataPivotCell) {
    this.setElementSelected(dataPivotCell.element, dataPivotCell.scheduleId, dataPivotCell.isAltRow);

    this.pendingSelectInEffect = true;
    this.pendingSelectCellStart = dataPivotCell;
    this.pendingSelectCellEnd = dataPivotCell;
    this.updatePendingSelectRange();
  }

  finalizePendingSelect(dataPivotCell) {
    if (dataPivotCell != null && !this.isCellSelected(dataPivotCell.scheduleId, dataPivotCell.dataField))
      this.setElementHovered(dataPivotCell.element, dataPivotCell.scheduleId, dataPivotCell.isAltRow);

    if (!this.pendingSelectInEffect)
      return;

    this.mergeSelectRange(this.pendingSelectRange);

    this.pendingSelectInEffect = false;
    this.pendingSelectCellStart = null;
    this.pendingSelectCellEnd = null;
    this.pendingSelectRange = {};
  }

  mergeSelectRange(selectRange) {
    CoreUtils.iterateProperties(selectRange, (selectKey, selectCell) => {
      if (this.currentSelectRange[selectKey] === undefined)
        this.currentSelectRange[selectKey] = selectCell;
    });
  }

  getCurrentSelectionCellCount() {
    var cellCount = 0;
    CoreUtils.iterateProperties(this.currentSelectRange, (_, __) => cellCount++);
    return cellCount;
  }

  getCurrentSelection() {
    var selectionIndex = {};

    CoreUtils.iterateProperties(this.currentSelectRange, (_, selectCell) => {
      var dataFields = selectionIndex[selectCell.scheduleId];
      if (dataFields === undefined) {
        dataFields = [];
        selectionIndex[selectCell.scheduleId] = dataFields;
      }

      dataFields.push(selectCell.dataField);
    });

    var currentSelection = [];
    CoreUtils.iterateProperties(selectionIndex, (scheduleId, dataFields) => {
      currentSelection.push({
        scheduleId: scheduleId,
        dataFields: orderBy(dataFields)
      });
    });

    return orderBy(currentSelection);
  }

  clearCurrentSelection() {
    CoreUtils.iterateProperties(this.currentSelectRange, (_, selectCell) => {
      var element = this.getCellElement(selectCell.scheduleId, selectCell.dataField);
      this.setElementNormal(element, selectCell.isAltRow);
    });

    this.currentSelectRange = {};
  }

  updatePendingSelectRange() {
    var currentRange = this.pendingSelectRange;
    var updatedRange = this.pendingSelectInEffect ? this.determineSelectRange(this.pendingSelectCellStart, this.pendingSelectCellEnd) : {};

    CoreUtils.iterateProperties(currentRange, (selectKey, selectCell) => {
      if (updatedRange[selectKey] === undefined && this.currentSelectRange[selectKey] === undefined) {
        var element = this.getCellElement(selectCell.scheduleId, selectCell.dataField);
        this.setElementNormal(element, selectCell.isAltRow);
      }
    });

    CoreUtils.iterateProperties(updatedRange, (_, selectCell) => {
      var element = this.getCellElement(selectCell.scheduleId, selectCell.dataField);
      this.setElementSelected(element, selectCell.scheduleId, selectCell.isAltRow);
    });

    this.pendingSelectRange = updatedRange;
  }

  isOnlyCellSelected(scheduleId, dataField) {
    return this.isCellSelected(scheduleId, dataField) && this.getCurrentSelectionCellCount() === 1;
  }

  isCellSelected(scheduleId, dataField) {
    var selectKey = this.createSelectKey(scheduleId, dataField);
    return this.pendingSelectRange[selectKey] !== undefined ||
      this.currentSelectRange[selectKey] !== undefined;
  }

  determineSelectRange(pendingSelectCellStart, pendingSelectCellEnd) {
    var startX = pendingSelectCellStart.columnIndex;
    var endX = pendingSelectCellEnd.columnIndex;
    var startY = pendingSelectCellStart.rowIndex;
    var endY = pendingSelectCellEnd.rowIndex;

    if (endX < startX) {
      var tempX = endX;
      endX = startX;
      startX = tempX;
    }

    if (endY < startY) {
      var tempY = endY;
      endY = startY;
      startY = tempY;
    }

    var selectRange = {};

    for (var currentY = startY; currentY <= endY; currentY++) {
      var scheduleId = this.getKeyByVisibleRowIndex(currentY);
      if (typeof scheduleId !== 'string')
        continue;

      if (!this.isRowSelectable(scheduleId))
        continue;

      for (var currentX = startX; currentX <= endX; currentX++) {
        var dataField = this.getDataFieldByVisibleColumnIndex(currentX);

        var selectCell = {
          selectKey: this.createSelectKey(scheduleId, dataField),
          scheduleId: scheduleId,
          dataField: dataField,
          isAltRow: this.isAltRowIndex[currentY]
        };

        selectRange[selectCell.selectKey] = selectCell;
      }
    }

    return selectRange;
  }

  isRowSelectable(scheduleId) {
    return CoreUtils.isDefinedAndNotNull(this.onQuerySelectableRow)
      ? this.onQuerySelectableRow({
        scheduleId: scheduleId
      })
      : true;
  }

  getCellElement(scheduleId, dataField) {
    var dataGrid = this.resolveDataGrid();

    var rowIndex = dataGrid.getRowIndexByKey(scheduleId);
    return dataGrid.getCellElement(rowIndex, dataField);
  }

  getKeyByVisibleRowIndex(visibleRowIndex) {
    return this.resolveDataGrid().getKeyByRowIndex(visibleRowIndex);
  }

  getDataFieldByVisibleColumnIndex(visibleColumnIndex) {
    return this.resolveDataGrid().getVisibleColumns()[visibleColumnIndex].dataField;
  }

  createSelectKey(scheduleId, dataField) {
    return scheduleId + '_' + dataField;
  }

  setElementNormal(element) {
    if (element.classList === undefined)
      return;

    DomUtils.replaceClassName(
      element.classList,
      'pwx-select',
      'pwx-select-none'
    );
  }

  setElementHovered(element, scheduleId, isAltRow) {
    if (element.classList === undefined)
      return;

    if (!this.isRowSelectable(scheduleId))
      return;

    DomUtils.replaceClassName(
      element.classList,
      'pwx-select',
      isAltRow ? 'pwx-select-hover-alt' : 'pwx-select-hover'
    );
  }

  setElementSelected(element, scheduleId, isAltRow) {
    if (element.classList === undefined)
      return;

    if (!this.isRowSelectable(scheduleId))
      return;

    DomUtils.replaceClassName(
      element.classList,
      'pwx-select',
      isAltRow ? 'pwx-select-full-alt' : 'pwx-select-full'
    );
  }

  handleTooltipTracking(event) {
    if (this.pendingSelectInEffect)
      return;

    var toolTipCell = {
      scheduleId: event.data[this.keyProperty],
      dataField: event.column.dataField,
      element: event.cellElement
    };

    if (event.eventType === 'mouseover') {
      this.toolTipCell = toolTipCell;

      var recentToolTip = this.toolTipHideTimeout !== null;
      this.cancelToolTipHideTimeout();

      if (recentToolTip)
        this.showToolTip();
      else {
        this.toolTipShowTimeout = setTimeout(this.showToolTip.bind(this), this.toolTipDelayMs);
        this.toolTipActive = false;
      }
    } else {
      this.hideToolTip();
    }
  }

  hideToolTip() {
    if (this.toolTipCell === null)
      return;

    this.cancelToolTipShowTimeout();
    this.cancelToolTipHideTimeout();

    if (this.toolTipActive) {
      if (CoreUtils.isDefinedAndNotNull(this.onHideFieldTooltip))
        this.onHideFieldTooltip(this.toolTipCell);

      this.toolTipHideTimeout = setTimeout(this.toolTipHideTimeoutExpired.bind(this), toolTipDebounceDelayMs);
    }

    this.toolTipActive = false;
    this.toolTipCell = null;
  }

  showToolTip() {
    this.toolTipShowTimeout = null;
    this.toolTipActive = true;

    var showResult = true;

    if (CoreUtils.isDefinedAndNotNull(this.onShowFieldTooltip))
      showResult = this.onShowFieldTooltip(this.toolTipCell);

    if (showResult === false) {
      this.toolTipCell = null;
      this.toolTipActive = false;
    } else
      this.toolTipActive = true;
  }

  cancelToolTipShowTimeout() {
    if (this.toolTipShowTimeout === null)
      return;

    clearTimeout(this.toolTipShowTimeout);
    this.toolTipShowTimeout = null;
  }

  cancelToolTipHideTimeout() {
    if (this.toolTipHideTimeout === null)
      return;

    clearTimeout(this.toolTipHideTimeout);
    this.toolTipHideTimeout = null;
  }

  toolTipHideTimeoutExpired() {
    this.toolTipHideTimeout = null;
  }
}